import type {
  DesvioLote,
  QualidadeLote,
  QualidadePayload,
  QualidadeReprocessoMes,
  StatusLote,
  TipoLote,
} from '@/types/dashboards';
import { fmtEspera } from './painelFormat';

/**
 * Seletores puros da aba Liberação da Qualidade.
 *
 * Status, prazo, tempo de espera e máquina chegam prontos do backend. Aqui só se recorta
 * pelo filtro da tela (tipo, meses, máquina) e se agrega — média e p90 com a mesma regra
 * do backend (p90 por posição: um prazo que algum lote de fato teve).
 */

export const TODAS_MAQUINAS = 'todas';

export interface FiltroQualidade {
  tipo: TipoLote;
  meses: string[];
  maquina: string;
}

const daMaquina = (lote: QualidadeLote, maquina: string) => maquina === TODAS_MAQUINAS || lote.maquina === maquina;

/** Lotes que entraram no CQ nos meses escolhidos (pendente antigo fica fora — está na fila). */
export function lotesDoRecorte(payload: QualidadePayload, filtro: FiltroQualidade): QualidadeLote[] {
  return payload.lotes.filter(
    (l) =>
      l.tipo === filtro.tipo &&
      daMaquina(l, filtro.maquina) &&
      !l.fora_da_janela &&
      l.mes !== null &&
      filtro.meses.includes(l.mes),
  );
}

/**
 * Lotes do mês a mês (prazo por mês, resumo por mês): os do período e os do histórico (até 6
 * meses antes do fim do período, desde 27/10/2026). Pendente antigo, de antes do histórico, fica
 * fora — está na fila.
 */
export function lotesDoHistorico(payload: QualidadePayload, filtro: Omit<FiltroQualidade, 'meses'>): QualidadeLote[] {
  return payload.lotes.filter(
    (l) => l.tipo === filtro.tipo && daMaquina(l, filtro.maquina) && l.mes !== null && (!l.fora_da_janela || !!l.historico),
  );
}

/**
 * Está na fila "aguardando CQ": o lote com saldo no depósito do CQ, uma linha por lote (regra de
 * 15/10/2026, como o CE0811). Snapshot de antes disso não tem `na_fila`: vale a ficha pendente.
 */
export const naFila = (l: QualidadeLote): boolean => l.na_fila ?? l.status === 'pendente';

/** Quantidade do lote na fila: o saldo no depósito (snapshot antigo: a quantidade da ficha). */
export const qtdFila = (l: QualidadeLote): number => l.saldo_deposito ?? l.qt_original;

/** A fila de hoje: todo lote do tipo e da máquina com saldo no CQ, de qualquer mês. */
export function filaAtual(payload: QualidadePayload, filtro: FiltroQualidade): QualidadeLote[] {
  return payload.lotes.filter((l) => l.tipo === filtro.tipo && daMaquina(l, filtro.maquina) && naFila(l));
}

/** Fichas pendentes com o lote fora do CQ, da mais antiga para a mais nova: a Qualidade encerra no Datasul. */
export function fichasSemSaldo(payload: QualidadePayload, filtro: Omit<FiltroQualidade, 'meses'>): QualidadeLote[] {
  return payload.lotes
    .filter((l) => l.tipo === filtro.tipo && daMaquina(l, filtro.maquina) && l.status === 'sem_saldo')
    .sort((a, b) => (a.entrada_cq ?? '').localeCompare(b.entrada_cq ?? '') || a.ficha - b.ficha);
}

export function percentil(valores: number[], p: number): number | null {
  if (valores.length === 0) return null;
  const ordenados = [...valores].sort((a, b) => a - b);
  return ordenados[Math.max(0, Math.ceil(p * ordenados.length) - 1)];
}

const media = (valores: number[]): number | null =>
  valores.length === 0 ? null : Math.round((valores.reduce((a, b) => a + b, 0) / valores.length) * 10) / 10;

const prazos = (lotes: QualidadeLote[]): number[] =>
  lotes.map((l) => l.prazo_dias).filter((d): d is number => d !== null);

const somaQtd = (lotes: QualidadeLote[]): number => lotes.reduce((acc, l) => acc + l.qt_original, 0);

export interface KpisQualidade {
  lotes: number;
  quantidade: number;
  liberados: number;
  pctLiberados: number | null;
  rejeicaoPendente: number;
  condicionais: number;
  prazoMedio: number | null;
  prazoMediana: number | null;
  prazoP90: number | null;
  filaTotal: number;
  filaForaDaJanela: number;
  filaQuantidade: number;
  /** Lotes que saíram do CQ (têm prazo real) e foram avaliados contra a previsão. */
  saidasComPrevisao: number;
  saidasNoPrazo: number;
  pctNoPrazo: number | null;
  /** Lotes na fila que já passaram da previsão. */
  filaAtrasados: number;
}

/** Saiu do CQ (tem prazo real) e tem previsão: entra na conta do "no prazo". */
const saiuComPrevisao = (l: QualidadeLote): boolean => l.prazo_dias !== null && !!l.desvio;

export function kpisQualidade(recorte: QualidadeLote[], fila: QualidadeLote[]): KpisQualidade {
  const liberados = recorte.filter((l) => l.status === 'liberada').length;
  const p = prazos(recorte);
  const saidas = recorte.filter(saiuComPrevisao);
  const noPrazo = saidas.filter((l) => l.desvio === 'no_prazo').length;
  return {
    lotes: recorte.length,
    quantidade: somaQtd(recorte),
    liberados,
    pctLiberados: recorte.length ? Math.round((liberados / recorte.length) * 1000) / 10 : null,
    rejeicaoPendente: recorte.filter((l) => l.status === 'rejeicao_pendente').length,
    condicionais: recorte.filter((l) => l.condicional).length,
    prazoMedio: media(p),
    prazoMediana: percentil(p, 0.5),
    prazoP90: percentil(p, 0.9),
    filaTotal: fila.length,
    filaForaDaJanela: fila.filter((l) => l.fora_da_janela).length,
    filaQuantidade: fila.reduce((acc, l) => acc + qtdFila(l), 0),
    saidasComPrevisao: saidas.length,
    saidasNoPrazo: noPrazo,
    pctNoPrazo: saidas.length ? Math.round((noPrazo / saidas.length) * 1000) / 10 : null,
    filaAtrasados: fila.filter((l) => l.desvio === 'atrasado').length,
  };
}

export interface ResumoReprocesso {
  lotes: number;
  produzidos: number;
  pct: number | null;
  quantidade: number;
}

/** Lotes reprocessados nos meses escolhidos; `null` sem o dado (snapshot antigo). */
export function reprocessoDoRecorte(payload: QualidadePayload, meses: string[]): ResumoReprocesso | null {
  if (!payload.reprocesso) return null;
  const doRecorte = payload.reprocesso.filter((m) => meses.includes(m.mes));
  const lotes = doRecorte.reduce((acc, m) => acc + m.lotes_reprocessados, 0);
  const produzidos = doRecorte.reduce((acc, m) => acc + m.lotes_produzidos, 0);
  return {
    lotes,
    produzidos,
    pct: produzidos ? Math.round((lotes / produzidos) * 1000) / 10 : null,
    quantidade: doRecorte.reduce((acc, m) => acc + m.quantidade_reprocessada, 0),
  };
}

/** Mês → linha de reprocesso, para a tabela mês a mês. */
export const reprocessoPorMes = (payload: QualidadePayload): Map<string, QualidadeReprocessoMes> =>
  new Map((payload.reprocesso ?? []).map((m) => [m.mes, m]));

export interface BarraFila {
  faixa: string;
  rotulo: string;
  lotes: number;
  quantidade: number;
}

export function filaPorFaixa(payload: QualidadePayload, fila: QualidadeLote[]): BarraFila[] {
  return payload.faixas_espera.map(({ faixa, rotulo }) => {
    const daFaixa = fila.filter((l) => l.faixa_espera === faixa);
    return { faixa, rotulo, lotes: daFaixa.length, quantidade: daFaixa.reduce((acc, l) => acc + qtdFila(l), 0) };
  });
}

export interface PrazoAgrupado {
  chave: string;
  rotulo: string;
  lotes: number;
  comPrazo: number;
  prazoMedio: number | null;
  prazoP90: number | null;
}

const agrupar = (chave: string, rotulo: string, lotes: QualidadeLote[]): PrazoAgrupado => {
  const p = prazos(lotes);
  return { chave, rotulo, lotes: lotes.length, comPrazo: p.length, prazoMedio: media(p), prazoP90: percentil(p, 0.9) };
};

/** Prazo por máquina no recorte, do mais lento para o mais rápido. Máquina sem lote liberado fica de fora. */
export function prazoPorMaquina(recorte: QualidadeLote[]): PrazoAgrupado[] {
  const nomes = [...new Set(recorte.map((l) => l.maquina))];
  return nomes
    .map((m) => agrupar(m, m, recorte.filter((l) => l.maquina === m)))
    .filter((g) => g.prazoMedio !== null)
    .sort((a, b) => (b.prazoMedio ?? 0) - (a.prazoMedio ?? 0) || a.rotulo.localeCompare(b.rotulo));
}

/** Prazo mês a mês, na ordem do calendário: o período e o histórico de antes dele. */
export function prazoPorMes(payload: QualidadePayload, filtro: Omit<FiltroQualidade, 'meses'>): PrazoAgrupado[] {
  const lotes = lotesDoHistorico(payload, filtro);
  return payload.meses.filter((m) => !m.futuro).map((m) => agrupar(m.key, m.label, lotes.filter((l) => l.mes === m.key)));
}

export interface LinhaResumoQualidade extends PrazoAgrupado {
  emAndamento: boolean;
  liberados: number;
  pendentes: number;
  rejeicaoPendente: number;
  semSaldo: number;
  cancelados: number;
  condicionais: number;
  quantidade: number;
}

/** Resumo de todos os meses do snapshot (período e histórico) para o tipo e a máquina escolhidos. */
export function resumoMensal(payload: QualidadePayload, filtro: Omit<FiltroQualidade, 'meses'>): LinhaResumoQualidade[] {
  const recorte = lotesDoHistorico(payload, filtro);
  return payload.meses
    .filter((m) => !m.futuro)
    .map((m) => {
      const doMes = recorte.filter((l) => l.mes === m.key);
      const conta = (s: StatusLote) => doMes.filter((l) => l.status === s).length;
      return {
        ...agrupar(m.key, m.label, doMes),
        emAndamento: m.em_andamento,
        liberados: conta('liberada'),
        pendentes: conta('pendente'),
        rejeicaoPendente: conta('rejeicao_pendente'),
        semSaldo: conta('sem_saldo'),
        cancelados: conta('cancelada'),
        condicionais: doMes.filter((l) => l.condicional).length,
        quantidade: somaQtd(doMes),
      };
    });
}

export type FiltroStatus = 'todos' | StatusLote;
export type FiltroDesvio = 'todos' | DesvioLote;

export interface FiltroTabela {
  busca: string;
  status: FiltroStatus;
  faixa: string;
  /** Passou ou não da previsão de liberação; sem o campo, todos. */
  desvio?: FiltroDesvio;
}

const casaDesvio = (l: QualidadeLote, desvio: FiltroDesvio | undefined): boolean =>
  !desvio || desvio === 'todos' || l.desvio === desvio;

const noPeriodoDaTabela = (l: QualidadeLote, filtro: FiltroQualidade): boolean => {
  if (l.tipo !== filtro.tipo || !daMaquina(l, filtro.maquina)) return false;
  if (naFila(l) && l.fora_da_janela) return true;
  return !l.fora_da_janela && l.mes !== null && filtro.meses.includes(l.mes);
};

const casaBusca = (l: QualidadeLote, payload: QualidadePayload, termo: string): boolean => {
  if (!termo) return true;
  const semPonto = termo.replace(/\./g, '');
  const descricao = payload.itens[l.codigo_datasul]?.descricao.toLowerCase() ?? '';
  return (
    l.lote.toLowerCase().includes(termo) ||
    (semPonto !== '' && l.codigo_datasul.replace(/\./g, '').includes(semPonto)) ||
    descricao.includes(termo)
  );
};

/**
 * Linhas da tabela, na ordem do backend (fila primeiro, do que espera há mais tempo). Lote
 * na fila com entrada antes da janela aparece mesmo fora dos meses escolhidos: é justamente o
 * lote que ninguém está vendo. Ficha sem saldo antiga fica na lista dela.
 */
export function lotesDaTabela(payload: QualidadePayload, filtro: FiltroQualidade, tabela: FiltroTabela): QualidadeLote[] {
  const termo = tabela.busca.trim().toLowerCase();
  return payload.lotes.filter(
    (l) =>
      noPeriodoDaTabela(l, filtro) &&
      (tabela.status === 'todos' || l.status === tabela.status) &&
      (tabela.faixa === 'todas' || l.faixa_espera === tabela.faixa) &&
      casaDesvio(l, tabela.desvio) &&
      casaBusca(l, payload, termo),
  );
}

/**
 * O desvio em palavras, para a tabela: quem saiu do CQ ("No prazo", "Atrasou 3 dias") e quem
 * ainda está ("Faltam 5 dias", "Vence hoje", "Atrasado 2 dias"). `null` sem previsão.
 */
export function textoDesvio(l: QualidadeLote): string | null {
  if (!l.desvio || l.desvio_dias === null || l.desvio_dias === undefined) return null;
  const d = Math.abs(l.desvio_dias);
  // a ficha esquecida de 2020 vira "6,6 anos", não "2424 dias"
  const dias = d >= 60 ? fmtEspera(d).replace(/^há /, '') : `${d} ${d === 1 ? 'dia' : 'dias'}`;
  const saiu = l.prazo_dias !== null;
  if (l.desvio === 'atrasado') return saiu ? `Atrasou ${dias}` : `Atrasado ${dias}`;
  if (saiu) return 'No prazo';
  return d === 0 ? 'Vence hoje' : `Faltam ${dias}`;
}
